/* eslint-env browser */
// Security › Staff activity (wave 3, W2_Portal_Tables look; admin only): the
// internal staff with their time on the portal, events and IPs in the modern
// table (a row opens that person's timeline), and the activity feed with its
// filters as chips.
//
// Three sub-views, as before:
//   1. Staff overview — internal users with last active + event counts.
//   2. Activity feed — chronological event log, filterable (first 200 shown).
//   3. User detail — one staff member's timeline grouped by session, their
//      IPs (new ones flagged) and figures.
//
// Data, exactly as before: ONE bounded read of /staffActivityLogs from
// (now − lookback days), ordered by timestamp (a get, no live listener),
// again when the lookback changes or Refresh is pressed; the users list
// already in the store for names and photos. Nothing is written. Every time
// is Addis Ababa on a 12-hour clock (owner rule); "today" starts at
// midnight Addis Ababa.
// Call-centre feedback (29 Oct 2026): the feed's event-type and window chips
// fold away with "Hide filters" (kept per person in this browser); while
// they are hidden the ones in use show as removable chips under the search.
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getDatabase, ref, query, orderByChild, startAt, get } from 'firebase/database';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { tokens as c } from '../theme';
import { EmptyState, InlineMessage } from '../components/feedback';
import DashboardCard from '../components/DashboardCard';
import { ADDIS_OFFSET_MS, formatAddisDate, formatAddisDateTime } from '../common/exportUtils';
import {
  ActiveFilters,
  BackButton,
  ChipGroup,
  DataTable,
  DetailCard,
  DropChip,
  FilterToggleButton,
  IconCircle,
  InfoRow,
  PageHeader,
  SearchField,
  StatusChip,
  ALL,
  useFiltersOpen,
} from '../components/modern';
import { STAFF_USER_TYPES } from '../services/staffActivityLogger';

// How far back to load by default. Pulls the most recent 7 days into
// memory; admin can use the "Load 30 days" button for the full retention
// window. Bandwidth-wise: 30 days × 20 staff × 65 events/day × ~500 B
// per entry ≈ 19 MB worst case — fine but we don't pre-load it.
const DEFAULT_LOOKBACK_DAYS = 7;
const MAX_LOOKBACK_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

// One icon and one hue per event type (icon + word, never colour alone).
const EVENT_TYPE_META = {
  page_view: { labelKey: 'staff_activity_page_view', label: 'Page view', icon: VisibilityOutlinedIcon, hue: 'info', tone: 'info' },
  download: { labelKey: 'staff_activity_download', label: 'Download', icon: FileDownloadOutlinedIcon, hue: 'amber', tone: 'amber' },
  action: { labelKey: 'staff_activity_action', label: 'Action', icon: TouchAppOutlinedIcon, hue: 'accent', tone: 'accent' },
};

const ROLE_TONE = { finance: 'info', callcenteragent: 'success', callcentersupervisor: 'accent' };

// Idle cap for time-on-portal calculation. With no heartbeats, time
// is derived purely from gaps between navigation / action events.
// We cap each gap at 5 min — a reasonable upper bound for "still
// engaged with the previous page" before we count the user as gone.
// This deliberately under-counts users who read a single page for a
// long time; explicit choice to save on RTDB writes.
const ENGAGEMENT_IDLE_CAP_MS = 5 * 60 * 1000;

// Format milliseconds as a human duration: "2h 14m", "47m", "12s".
const formatDuration = (ms) => {
  if (!ms || ms < 1000) return '0m';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${totalSec}s`;
};

// Sum gaps between consecutive ascending event timestamps within ONE
// session, capping each gap at ENGAGEMENT_IDLE_CAP_MS so idle stretches
// don't inflate the number. Single-event sessions get a 30s floor.
const computeSessionEngagedMs = (timestampsAsc) => {
  if (!timestampsAsc || timestampsAsc.length === 0) return 0;
  if (timestampsAsc.length === 1) return 30 * 1000;
  let total = 0;
  for (let i = 1; i < timestampsAsc.length; i++) {
    const gap = timestampsAsc[i] - timestampsAsc[i - 1];
    if (gap <= 0) continue;
    total += Math.min(gap, ENGAGEMENT_IDLE_CAP_MS);
  }
  return total;
};

// Midnight Addis Ababa of today.
const addisStartOfToday = () => Math.floor((Date.now() + ADDIS_OFFSET_MS) / DAY_MS) * DAY_MS - ADDIS_OFFSET_MS;

const pad2 = (n) => (n < 10 ? `0${n}` : String(n));
// "25 Oct 2026, 2:31:07 pm" (Addis Ababa, 12-hour, with seconds for the log).
const addisStamp = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms + ADDIS_OFFSET_MS);
  const h = d.getUTCHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${formatAddisDate(ms)}, ${h12}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())} ${h < 12 ? 'am' : 'pm'}`;
};

const initialOf = (name) => (name || '?').trim().charAt(0).toUpperCase();

export default function StaffActivity() {
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const usersdata = useSelector((state) => state.usersdata);
  const loggingEnabled = useSelector((state) => state.settingsdata?.settings?.enableStaffActivityLogging !== false);

  const isAdmin = auth?.profile?.usertype === 'admin';
  const [lookbackDays, setLookbackDays] = useState(DEFAULT_LOOKBACK_DAYS);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Sub-view: 'overview' (default) | 'feed' | 'user'
  const [view, setView] = useState('overview');
  const [selectedUid, setSelectedUid] = useState(null);

  // Feed filters
  const [searchText, setSearchText] = useState('');
  const [filterUser, setFilterUser] = useState(ALL);
  const [filterEventType, setFilterEventType] = useState(ALL);
  const [filterDays, setFilterDays] = useState(lookbackDays);
  // The event-type and window chips fold away (this person's choice).
  const [filtersOpen, toggleFilters] = useFiltersOpen('staff_activity_feed');

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  // Read logs from (now - lookbackDays) as a one-shot get() — the previous
  // live onValue re-downloaded the whole window on every staff write.
  // Re-runs when the lookback changes or the Refresh button is pressed.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const db = getDatabase();
    const since = Date.now() - lookbackDays * 24 * 60 * 60 * 1000;
    const q = query(ref(db, 'staffActivityLogs'), orderByChild('timestamp'), startAt(since));
    get(q)
      .then((snap) => {
        if (cancelled) return;
        const val = snap.val() || {};
        const arr = Object.keys(val).map((id) => Object.assign({}, val[id], { id }));
        arr.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setLogs(arr);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[StaffActivity] staffActivityLogs read failed:', err);
        if (!cancelled) {
          setLoading(false);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, lookbackDays, refreshTick]);

  // Build per-user aggregate stats (for the overview list). Computes: last
  // seen, totals by type, AND time-on-portal (engaged duration). Time-on-
  // portal is derived by grouping each user's events into sessions, then
  // summing capped gaps between consecutive events in each session — gives
  // a faithful "actually engaged" number rather than a raw login-to-logout
  // window.
  const staffAggregates = useMemo(() => {
    const map = {};
    // Pre-bucket logs by (uid, sessionId) to compute session
    // durations cleanly. Two passes is simpler than mutating
    // running totals as we go.
    const sessionBuckets = {}; // { uid: { sessionId: [ts,ts,...] } }
    const startOfToday = addisStartOfToday();

    logs.forEach((log) => {
      const uid = log.uid;
      if (!uid) return;
      if (!map[uid]) {
        map[uid] = {
          uid,
          userName: log.userName || '',
          userType: log.userType || '',
          userEmail: log.userEmail || '',
          total: 0,
          pageViews: 0,
          downloads: 0,
          actions: 0,
          lastSeen: 0,
          today: 0,
          timeOnPortalMs: 0,
          timeOnPortalTodayMs: 0,
          sessionCount: 0,
        };
      }
      const m = map[uid];
      m.total++;
      if (log.eventType === 'page_view') m.pageViews++;
      else if (log.eventType === 'download') m.downloads++;
      else if (log.eventType === 'action') m.actions++;
      if ((log.timestamp || 0) > m.lastSeen) m.lastSeen = log.timestamp || 0;
      if ((log.timestamp || 0) >= startOfToday) m.today++;

      const sid = log.sessionId || 'no-session';
      if (!sessionBuckets[uid]) sessionBuckets[uid] = {};
      if (!sessionBuckets[uid][sid]) sessionBuckets[uid][sid] = [];
      sessionBuckets[uid][sid].push(log.timestamp || 0);
    });

    // Pass 2: per user × per session, sort timestamps ascending and
    // compute engaged duration (with idle-cap). Also compute the
    // engaged-today slice by re-running on the today-only subset.
    Object.keys(sessionBuckets).forEach((uid) => {
      const sessions = sessionBuckets[uid];
      const m = map[uid];
      Object.keys(sessions).forEach((sid) => {
        const tsAll = sessions[sid].filter((ts) => ts > 0).sort((a, b) => a - b);
        if (tsAll.length === 0) return;
        m.sessionCount++;
        m.timeOnPortalMs += computeSessionEngagedMs(tsAll);
        const tsToday = tsAll.filter((ts) => ts >= startOfToday);
        if (tsToday.length > 0) {
          m.timeOnPortalTodayMs += computeSessionEngagedMs(tsToday);
        }
      });
    });

    // Pass 3: per-user IP analysis. We split the lookback window in
    // half: IPs that ONLY appear in the recent half (and were never
    // seen in the baseline half) are flagged as new. This is a
    // best-effort comparison using only the data already loaded.
    const windowMs = lookbackDays * 24 * 60 * 60 * 1000;
    const baselineCutoff = Date.now() - windowMs / 2;
    const ipBuckets = {}; // uid -> { baseline: Set, recent: Set, latest: { ip, ts } }
    logs.forEach((log) => {
      if (!log.uid || !log.clientIp) return;
      if (!ipBuckets[log.uid]) {
        ipBuckets[log.uid] = { baseline: new Set(), recent: new Set(), latest: { ip: null, ts: 0 } };
      }
      const b = ipBuckets[log.uid];
      if ((log.timestamp || 0) < baselineCutoff) {
        b.baseline.add(log.clientIp);
      } else {
        b.recent.add(log.clientIp);
      }
      if ((log.timestamp || 0) > b.latest.ts) {
        b.latest = { ip: log.clientIp, ts: log.timestamp || 0 };
      }
    });
    Object.keys(ipBuckets).forEach((uid) => {
      if (!map[uid]) return;
      const b = ipBuckets[uid];
      const newIps = [];
      b.recent.forEach((ip) => {
        if (!b.baseline.has(ip)) newIps.push(ip);
      });
      map[uid].latestIp = b.latest.ip;
      map[uid].knownIps = Array.from(b.baseline);
      map[uid].recentIps = Array.from(b.recent);
      map[uid].newIps = newIps; // present in recent but not in baseline
      map[uid].ipChanged = newIps.length > 0 && b.baseline.size > 0; // only flag if there IS a baseline
    });

    // Also surface known staff users who haven't logged ANY activity
    // in the window — admin needs to see "this person hasn't been
    // active" too, not just the active ones. Also cross-references
    // every user (active or not) with usersdata to pull their
    // profile_image so the admin gets a face to identify each row.
    const usersById = {};
    if (usersdata?.users) {
      usersdata.users.forEach((u) => {
        if (u && u.id) usersById[u.id] = u;
      });
      usersdata.users.forEach((u) => {
        if (!u || STAFF_USER_TYPES.indexOf(u.usertype) < 0) return;
        if (!map[u.id]) {
          map[u.id] = {
            uid: u.id,
            userName: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '(no name)',
            userType: u.usertype,
            userEmail: u.email || '',
            total: 0,
            pageViews: 0,
            downloads: 0,
            actions: 0,
            lastSeen: 0,
            today: 0,
            timeOnPortalMs: 0,
            timeOnPortalTodayMs: 0,
            sessionCount: 0,
          };
        }
      });
    }
    // Attach profile_image (and refresh name from canonical source).
    Object.keys(map).forEach((uid) => {
      const u = usersById[uid];
      if (!u) return;
      map[uid].profileImage = u.profile_image || null;
      // Prefer the fresh name from /users over whatever was logged
      // (the user may have updated their profile since logging in).
      const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim();
      if (fullName) map[uid].userName = fullName;
      if (u.email && !map[uid].userEmail) map[uid].userEmail = u.email;
    });

    return Object.values(map).sort((a, b) => b.lastSeen - a.lastSeen);
  }, [logs, usersdata?.users, lookbackDays]);

  // Filtered logs for the feed tab.
  const filteredLogs = useMemo(() => {
    let rows = logs;
    if (filterUser !== ALL) rows = rows.filter((l) => l.uid === filterUser);
    if (filterEventType !== ALL) rows = rows.filter((l) => l.eventType === filterEventType);
    if (filterDays && filterDays !== lookbackDays) {
      const since = Date.now() - filterDays * 24 * 60 * 60 * 1000;
      rows = rows.filter((l) => (l.timestamp || 0) >= since);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      rows = rows.filter(
        (l) =>
          (l.userName || '').toLowerCase().includes(q) ||
          (l.userEmail || '').toLowerCase().includes(q) ||
          (l.action || '').toLowerCase().includes(q) ||
          (l.page || '').toLowerCase().includes(q) ||
          (l.category || '').toLowerCase().includes(q) ||
          (l.target && l.target.label && l.target.label.toLowerCase().includes(q))
      );
    }
    return rows;
  }, [logs, filterUser, filterEventType, filterDays, lookbackDays, searchText]);

  // Photos for the feed, looked up once per list (not per row).
  const photoById = useMemo(() => {
    const out = {};
    (usersdata?.users || []).forEach((u) => {
      if (u && u.id) out[u.id] = u.profile_image || null;
    });
    return out;
  }, [usersdata?.users]);

  const formatRelative = (ts) => {
    if (!ts) return L('staff_activity_never', 'Never');
    const diff = Date.now() - ts;
    if (diff < 60_000) return L('time_just_now', 'just now');
    if (diff < 3_600_000) {
      const mins = Math.floor(diff / 60_000);
      return t('minutes_ago', { count: mins, defaultValue_one: '{{count}} minute ago', defaultValue_other: '{{count}} minutes ago', defaultValue: '{{count}} minutes ago' });
    }
    if (diff < 86_400_000) {
      const hrs = Math.floor(diff / 3_600_000);
      return t('time_hours_ago', { count: hrs, defaultValue_one: '{{count}} hour ago', defaultValue_other: '{{count}} hours ago', defaultValue: '{{count}} hours ago' });
    }
    return formatAddisDateTime(ts);
  };

  const roleLabel = (type) => (type ? t(type, { defaultValue: type }) : '—');
  const eventLabel = (type) => {
    const meta = EVENT_TYPE_META[type];
    return meta ? L(meta.labelKey, meta.label) : type || '';
  };
  const windowLabel = (days) =>
    days === 1 ? L('staff_activity_last_24h', 'Last 24 hours') : t('staff_activity_last_days', { count: days, defaultValue: 'Last {{count}} days' });

  if (!isAdmin) {
    return (
      <InlineMessage kind="error" sx={{ my: 2 }}>
        {L('staff_activity_admin_only', 'Admin access required.')}
      </InlineMessage>
    );
  }

  const avatarOf = (name, image, type, size = 36) => (
    <Avatar
      src={image || undefined}
      alt=""
      sx={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.38),
        fontWeight: 700,
        bgcolor: c.SURFACE_ALT,
        color: c.INK_2,
        flex: 'none',
      }}
    >
      {initialOf(name)}
    </Avatar>
  );

  // ── User detail view ──────────────────────────────────────────────
  if (view === 'user' && selectedUid) {
    const userLogs = logs.filter((l) => l.uid === selectedUid);
    const user = staffAggregates.find((u) => u.uid === selectedUid);

    // Group adjacent events from the same session for a cleaner read.
    const grouped = [];
    let currentSession = null;
    userLogs.forEach((log) => {
      if (!currentSession || currentSession.sessionId !== log.sessionId) {
        currentSession = { sessionId: log.sessionId, events: [] };
        grouped.push(currentSession);
      }
      currentSession.events.push(log);
    });

    return (
      <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BackButton
          onClick={() => {
            setView('overview');
            setSelectedUid(null);
          }}
        />
        <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {avatarOf(user?.userName, user?.profileImage, user?.userType, 56)}
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: c.INK, overflowWrap: 'anywhere' }}>
              {user?.userName || L('staff_activity_unknown_user', 'Unknown user')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
              <StatusChip tone={ROLE_TONE[user?.userType] || 'neutral'} icon={<BadgeOutlinedIcon />} label={roleLabel(user?.userType)} />
              <Typography component="span" sx={{ fontSize: 13.5, color: c.INK_2, overflowWrap: 'anywhere' }}>
                {user?.userEmail || ''}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* IP / security panel — flags new IPs not seen in baseline. */}
        {user?.latestIp || (user?.knownIps && user.knownIps.length > 0) ? (
          <DetailCard icon={<PublicRoundedIcon />} hue={user?.ipChanged ? 'amber' : 'info'} title={L('staff_activity_ips', 'IP addresses')} list={false}>
            {user?.ipChanged ? (
              <InlineMessage kind="warning" title={L('staff_activity_new_ip_title', 'New IP detected')} sx={{ mb: 1.5 }}>
                {t('staff_activity_new_ip_body', {
                  count: user.newIps.length,
                  ips: user.newIps.join(', '),
                  defaultValue_one: 'This person signed in from {{count}} IP not seen in the first half of the lookback window: {{ips}}',
                  defaultValue_other: 'This person signed in from {{count}} IPs not seen in the first half of the lookback window: {{ips}}',
                  defaultValue: 'This person signed in from {{count}} IPs not seen in the first half of the lookback window: {{ips}}',
                })}
              </InlineMessage>
            ) : null}
            <Box component="dl" sx={{ m: 0 }}>
              <InfoRow label={L('staff_activity_current_ip', 'Current IP')}>
                <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: user?.ipChanged ? c.WARNING_TEXT : c.INK }}>
                  {user?.latestIp || '—'}
                </Box>
              </InfoRow>
              <InfoRow label={L('staff_activity_known_ips', 'Known IPs (baseline)')}>
                {user?.knownIps && user.knownIps.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {user.knownIps.map((ip) => (
                      <Chip key={ip} label={ip} size="small" sx={{ fontVariantNumeric: 'tabular-nums', bgcolor: c.SURFACE_ALT, color: c.INK }} />
                    ))}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: c.TEXT_3 }}>
                    {L('staff_activity_no_baseline', 'None recorded yet (no events in the first half of the window)')}
                  </Box>
                )}
              </InfoRow>
              <InfoRow label={L('staff_activity_recent_ips', 'Recent IPs')}>
                {user?.recentIps && user.recentIps.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {user.recentIps.map((ip) => {
                      const isNew = user.newIps && user.newIps.indexOf(ip) >= 0;
                      return (
                        <Chip
                          key={ip}
                          label={ip}
                          size="small"
                          icon={isNew ? <WarningAmberRoundedIcon /> : undefined}
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            bgcolor: isNew ? c.WARNING_TINT : c.SURFACE_ALT,
                            color: isNew ? c.WARNING_TEXT : c.INK,
                            '& .MuiChip-icon': { color: c.WARNING_TEXT },
                          }}
                        />
                      );
                    })}
                  </Box>
                ) : null}
              </InfoRow>
            </Box>
          </DetailCard>
        ) : null}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
          <DashboardCard icon={<ScheduleRoundedIcon />} hue="info" title={L('staff_activity_time_on_portal', 'Time on portal')} value={formatDuration(user?.timeOnPortalMs || 0)} />
          <DashboardCard icon={<ScheduleRoundedIcon />} hue="success" title={L('today', 'Today')} value={formatDuration(user?.timeOnPortalTodayMs || 0)} />
          <DashboardCard icon={<EventNoteOutlinedIcon />} hue="neutral" title={L('staff_activity_sessions_title', 'Sessions')} value={String(user?.sessionCount || 0)} />
          <DashboardCard
            icon={<TouchAppOutlinedIcon />}
            hue="accent"
            title={L('staff_activity_total_events', 'Total events')}
            value={String(user?.total || 0)}
            stats={[
              { label: eventLabel('page_view'), value: user?.pageViews || 0 },
              { label: eventLabel('download'), value: user?.downloads || 0 },
              { label: eventLabel('action'), value: user?.actions || 0 },
            ]}
          />
        </Box>

        {grouped.length === 0 ? (
          <Paper>
            <EmptyState
              compact
              icon={<EventNoteOutlinedIcon />}
              title={t('staff_activity_no_activity_days', { count: lookbackDays, defaultValue: 'No activity in the last {{count}} days.' })}
            />
          </Paper>
        ) : (
          grouped.map((session, sIdx) => (
            <Paper key={`${session.sessionId}-${sIdx}`} component="section" sx={{ p: 2 }}>
              <Typography component="h3" sx={{ fontSize: 13.5, fontWeight: 600, color: c.TEXT_3 }}>
                {t('staff_activity_session_started', {
                  time: formatAddisDateTime(session.events[session.events.length - 1].timestamp),
                  count: session.events.length,
                  defaultValue_one: 'Session · started {{time}} · {{count}} event',
                  defaultValue_other: 'Session · started {{time}} · {{count}} events',
                  defaultValue: 'Session · started {{time}} · {{count}} events',
                })}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {session.events.map((ev) => (
                  <EventRow key={ev.id} log={ev} eventLabel={eventLabel} />
                ))}
              </Box>
            </Paper>
          ))
        )}
      </Box>
    );
  }

  // ── Overview + Feed (toggled by view state) ───────────────────────
  const lookbackOptions = [1, 7, 14, MAX_LOOKBACK_DAYS].map((d) => ({ value: d, label: windowLabel(d) }));
  const viewOptions = [
    { value: 'overview', label: L('staff_activity_overview', 'Staff overview') },
    { value: 'feed', label: L('staff_activity_feed', 'Activity feed'), count: logs.length },
  ];

  const columns = [
    {
      id: 'userName',
      title: L('name', 'Name'),
      field: 'userName',
      searchValue: (s) => `${s.userName || ''} ${s.userEmail || ''}`,
      render: (s) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
          {avatarOf(s.userName, s.profileImage, s.userType, 32)}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{s.userName || '—'}</Box>
            <Box sx={{ fontSize: 12.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>{s.userEmail}</Box>
          </Box>
        </Box>
      ),
    },
    {
      id: 'userType',
      title: L('staff_activity_role', 'Role'),
      field: 'userType',
      searchValue: (s) => roleLabel(s.userType),
      sortValue: (s) => roleLabel(s.userType),
      render: (s) => <StatusChip tone={ROLE_TONE[s.userType] || 'neutral'} icon={<BadgeOutlinedIcon />} label={roleLabel(s.userType)} />,
    },
    {
      id: 'timeToday',
      title: L('staff_activity_time_today', 'Time today'),
      type: 'number',
      sortValue: (s) => s.timeOnPortalTodayMs,
      render: (s) => (
        <Box component="span" sx={{ fontWeight: s.timeOnPortalTodayMs > 0 ? 700 : 400, color: s.timeOnPortalTodayMs > 0 ? c.INK : c.TEXT_3 }}>
          {formatDuration(s.timeOnPortalTodayMs)}
        </Box>
      ),
    },
    {
      id: 'timePeriod',
      title: L('staff_activity_time_period', 'Time (period)'),
      type: 'number',
      sortValue: (s) => s.timeOnPortalMs,
      render: (s) => (
        <Box>
          <Box component="span" sx={{ color: s.timeOnPortalMs > 0 ? c.INK : c.TEXT_3 }}>
            {formatDuration(s.timeOnPortalMs)}
          </Box>
          {s.sessionCount > 0 ? (
            <Box sx={{ fontSize: 12, color: c.TEXT_3 }}>
              {t('staff_activity_sessions', { count: s.sessionCount, defaultValue_one: '{{count}} session', defaultValue_other: '{{count}} sessions', defaultValue: '{{count}} sessions' })}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      id: 'today',
      title: L('staff_activity_events_today', 'Events today'),
      field: 'today',
      type: 'number',
      render: (s) => (
        <Box component="span" sx={{ fontWeight: s.today > 0 ? 700 : 400 }}>
          {s.today}
        </Box>
      ),
    },
    {
      id: 'total',
      title: L('staff_activity_events_period', 'Events (period)'),
      field: 'total',
      type: 'number',
      render: (s) => (
        <Box>
          <Box component="span">{s.total}</Box>
          {s.total > 0 ? (
            <Box sx={{ fontSize: 12, color: c.TEXT_3, whiteSpace: 'nowrap' }}>
              {t('staff_activity_breakdown', {
                views: s.pageViews,
                downloads: s.downloads,
                actions: s.actions,
                defaultValue: '{{views}} views · {{downloads}} downloads · {{actions}} actions',
              })}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      id: 'lastSeen',
      title: L('staff_activity_last_seen', 'Last seen'),
      field: 'lastSeen',
      defaultSort: 'desc',
      sortValue: (s) => s.lastSeen || null,
      searchable: false,
      render: (s) => (
        <Box component="span" sx={{ color: s.lastSeen ? c.INK : c.TEXT_3, whiteSpace: 'nowrap' }}>
          {formatRelative(s.lastSeen)}
        </Box>
      ),
    },
    {
      id: 'latestIp',
      title: 'IP',
      field: 'latestIp',
      render: (s) =>
        s.latestIp ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
            {s.ipChanged ? (
              <Tooltip title={L('staff_activity_new_ip_tooltip', 'New IP — not seen in the baseline. New IPs: {{ips}}', { ips: s.newIps.join(', ') })}>
                <WarningAmberRoundedIcon tabIndex={0} aria-label={L('staff_activity_new_ip_title', 'New IP detected')} sx={{ fontSize: 18, color: c.WARNING_TEXT }} />
              </Tooltip>
            ) : (
              <PublicRoundedIcon aria-hidden="true" sx={{ fontSize: 16, color: c.TEXT_3 }} />
            )}
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: s.ipChanged ? c.WARNING_TEXT : c.INK, fontWeight: s.ipChanged ? 700 : 400 }}>
              {s.latestIp}
            </Box>
            {s.recentIps && s.recentIps.length > 1 ? (
              <Tooltip title={t('staff_activity_distinct_ips', { count: s.recentIps.length, defaultValue: '{{count}} distinct IPs in the recent window' })}>
                <Chip label={`+${s.recentIps.length - 1}`} size="small" tabIndex={0} sx={{ height: 20, fontSize: 11.5, bgcolor: c.SURFACE_ALT, color: c.INK_2 }} />
              </Tooltip>
            ) : null}
          </Box>
        ) : (
          <Box component="span" sx={{ color: c.TEXT_3 }}>
            —
          </Box>
        ),
    },
  ];

  const openUser = (s) => {
    setSelectedUid(s.uid);
    setView('user');
  };

  const userOptions = [{ value: ALL, label: L('staff_activity_all_staff', 'All staff') }].concat(
    staffAggregates.map((s) => ({ value: s.uid, label: s.userName || s.userEmail || s.uid }))
  );
  const typeOptions = [{ value: ALL, label: L('all', 'All') }].concat(Object.keys(EVENT_TYPE_META).map((k) => ({ value: k, label: eventLabel(k) })));
  const dayOptions = [1, 7, 14, 30].map((d) => ({ value: d, label: d === 1 ? '24h' : `${d}d` }));

  // The filters in the folding part, shown as removable chips while it is
  // folded (they stay applied).
  const eventTypeGroup = L('staff_activity_event_type', 'Event type');
  const windowGroup = L('staff_activity_window', 'Window');
  const foldedTokens = [];
  if (filterEventType !== ALL) foldedTokens.push({ key: 'type', group: eventTypeGroup, value: eventLabel(filterEventType) });
  if (filterDays && filterDays !== lookbackDays) {
    const day = dayOptions.find((o) => o.value === filterDays);
    foldedTokens.push({ key: 'window', group: windowGroup, value: day ? day.label : String(filterDays) });
  }
  const removeFoldedToken = (key) => {
    if (key === 'type') setFilterEventType(ALL);
    if (key === 'window') setFilterDays(lookbackDays);
  };
  const clearFoldedFilters = () => {
    setFilterEventType(ALL);
    setFilterDays(lookbackDays);
  };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!loggingEnabled ? (
        <InlineMessage kind="info" title={L('staff_activity_logging_off_title', 'Staff activity logging is off')}>
          {L(
            'staff_activity_logging_off_body',
            'New events are not being recorded. Turn “Enable staff activity logging” back on in Settings › General › Security to resume. The history below is still shown.'
          )}
        </InlineMessage>
      ) : null}

      {/* View switch, lookback and refresh */}
      <PageHeader secondaryActions={[{ key: 'refresh', label: L('refresh', 'Refresh'), icon: <RefreshRoundedIcon />, onClick: () => setRefreshTick((n) => n + 1), disabled: loading }]}>
        <DropChip label={L('staff_activity_lookback', 'Lookback')} value={lookbackDays} options={lookbackOptions} onChange={(v) => setLookbackDays(Number(v))} allValue={DEFAULT_LOOKBACK_DAYS} />
      </PageHeader>
      <ChipGroup value={view} options={viewOptions} onChange={setView} />

      {failed ? (
        <InlineMessage
          kind="error"
          title={L('staff_activity_load_failed', 'Could not load staff activity.')}
          actionLabel={L('retry', 'Retry')}
          onAction={() => setRefreshTick((n) => n + 1)}
        >
          {L('load_failed_body', 'Check the internet connection, then try again.')}
        </InlineMessage>
      ) : null}

      {view === 'overview' ? (
        <DataTable
          title={L('staff_activity_overview', 'Staff overview')}
          rows={staffAggregates}
          columns={columns}
          rowKey="uid"
          loading={loading && logs.length === 0}
          refreshing={loading && logs.length > 0}
          countText={({ shown, total, query: q, loading: busy }) =>
            busy
              ? L('staff_activity_loading', 'Loading staff activity…')
              : q
                ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
                : t('staff_activity_staff_count', {
                    count: total,
                    defaultValue_one: '{{count}} staff member',
                    defaultValue_other: '{{count}} staff members',
                    defaultValue: '{{count}} staff members',
                  })
          }
          subtitle={windowLabel(lookbackDays)}
          onRowClick={openUser}
          rowLabel={(s) => s.userName}
          actions={[{ key: 'view', label: L('view_details', 'View details'), icon: <PersonOutlineRoundedIcon />, inline: true, onClick: openUser }]}
          prefsKey="staff_activity"
          minWidth={1100}
          emptyState={({ query: q }) => (q ? null : { icon: <EventNoteOutlinedIcon />, title: L('staff_activity_empty', 'No staff activity recorded.') })}
        />
      ) : (
        <Paper component="section" aria-label={L('staff_activity_feed', 'Activity feed')} sx={{ minWidth: 0 }}>
          {/* Filters: the search and the user stay in sight; the chips fold away. */}
          <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: `1px solid ${c.OUTLINE}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <SearchField value={searchText} onChange={setSearchText} placeholder={L('staff_activity_search', 'Search name, action, page…')} />
              <DropChip label={L('staff_activity_user', 'User')} value={filterUser} options={userOptions} onChange={setFilterUser} />
              <FilterToggleButton
                open={filtersOpen}
                onClick={toggleFilters}
                activeCount={foldedTokens.length}
                controls="staff-feed-filters"
                sx={{ marginInlineStart: 'auto' }}
              />
            </Box>
            {!filtersOpen && foldedTokens.length ? (
              <ActiveFilters
                tokens={foldedTokens}
                onRemove={removeFoldedToken}
                onClearAll={foldedTokens.length > 1 ? clearFoldedFilters : undefined}
                sx={{ pt: 1.5 }}
              />
            ) : null}
            <Collapse in={filtersOpen} id="staff-feed-filters">
              <Box sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <ChipGroup label={eventTypeGroup} value={filterEventType} options={typeOptions} onChange={setFilterEventType} />
                <ChipGroup label={windowGroup} value={filterDays} options={dayOptions} onChange={(v) => setFilterDays(Number(v))} />
              </Box>
            </Collapse>
          </Box>
          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1 }}>
            {loading && logs.length === 0 ? (
              <Typography role="status" sx={{ color: c.TEXT_3, py: 3, textAlign: 'center' }}>
                {L('staff_activity_loading', 'Loading staff activity…')}
              </Typography>
            ) : filteredLogs.length === 0 ? (
              <EmptyState compact icon={<SearchOffRoundedIcon />} title={L('staff_activity_no_match', 'No events match your filters.')} />
            ) : (
              <Box>
                {filteredLogs.slice(0, 200).map((log) => (
                  <EventRow key={log.id} log={log} showUser profileImage={photoById[log.uid] || null} eventLabel={eventLabel} />
                ))}
                {filteredLogs.length > 200 ? (
                  <Typography sx={{ color: c.TEXT_3, textAlign: 'center', fontSize: 13, py: 1.5 }}>
                    {t('staff_activity_first_200', {
                      count: filteredLogs.length,
                      defaultValue: 'Showing the first 200 of {{count}} events. Narrow the filters to see more.',
                    })}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// Single-event row used in both Feed and User Detail views.
function EventRow({ log, showUser, profileImage, eventLabel }) {
  const meta = EVENT_TYPE_META[log.eventType] || { icon: PersonOutlineRoundedIcon, hue: 'neutral', tone: 'neutral' };
  const Icon = meta.icon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, borderBottom: `1px solid ${c.OUTLINE}`, '&:last-of-type': { borderBottom: 0 } }}>
      {/* Feed mode: show the user avatar so the row is scannable. */}
      {showUser ? (
        <Avatar src={profileImage || undefined} alt="" sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, flex: 'none', bgcolor: c.SURFACE_ALT, color: c.INK_2 }}>
          {initialOf(log.userName)}
        </Avatar>
      ) : (
        <IconCircle icon={<Icon />} hue={meta.hue} size={32} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* In feed mode, lead with the user's name then the action. */}
          {showUser && log.userName ? (
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: c.INK }}>
              {log.userName}
            </Typography>
          ) : null}
          <Typography component="span" sx={{ fontSize: 14, fontWeight: showUser ? 400 : 600, color: showUser ? c.INK_2 : c.INK, overflowWrap: 'anywhere' }}>
            {showUser ? '· ' : ''}
            {log.action}
          </Typography>
          {/* Type chip — quick visual cue in feed mode. */}
          {showUser ? <StatusChip tone={meta.tone} icon={<Icon />} label={eventLabel(log.eventType)} sx={{ height: 22, fontSize: 12 }} /> : null}
          {log.category ? <Chip label={log.category} size="small" sx={{ height: 22, fontSize: 12, bgcolor: c.SURFACE_ALT, color: c.INK_2 }} /> : null}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
          {log.page}
          {log.target && log.target.label ? ` · ${log.target.type}: ${log.target.label}` : ''}
          {log.clientIp ? (
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', marginInlineStart: 1 }}>
              {`· ${log.clientIp}`}
            </Box>
          ) : null}
        </Typography>
        {log.details ? (
          <Typography sx={{ fontSize: 12, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
            {Object.keys(log.details)
              .map((k) => `${k}=${log.details[k]}`)
              .join(' · ')}
          </Typography>
        ) : null}
      </Box>
      <Typography component="span" sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
        {addisStamp(log.timestamp)}
      </Typography>
    </Box>
  );
}
